import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserPlan } from "@/hooks/useUserPlan";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Shield, DollarSign, TrendingUp, TrendingDown,
  CreditCard, Loader2, RefreshCw, Banknote, Receipt, AlertCircle,
  CheckCircle, XCircle, Clock, Percent, PiggyBank,
} from "lucide-react";

// Cost constants (same as AdminCosts)
const FLASH_INPUT_COST_PER_1M = 0.075;
const FLASH_OUTPUT_COST_PER_1M = 0.30;
const PRO_INPUT_COST_PER_1M = 1.25;
const PRO_OUTPUT_COST_PER_1M = 10.00;
const TOKENS_PER_CHAR = 0.35;
const AVG_CHARS_PER_WORD = 6;
const USD_TO_BRL = 5.5;

// Simples Nacional tax rate (approximate for service companies)
const SIMPLES_TAX_RATE = 0.06; // 6% for Anexo III up to R$180k/year

interface FinancialData {
  month: string;
  revenue: {
    stripe_cents: number;
    pix_cents: number;
    total_cents: number;
    refunds_cents: number;
    net_cents: number;
  };
  costs: {
    stripe_fees_cents: number;
    ai_responses: number;
  };
  subscriptions: {
    active: number;
    canceled_this_month: number;
    canceled_details: Array<{
      id: string;
      customer: string;
      canceled_at: string | null;
    }>;
  };
  pix: {
    pending_count: number;
    approved_count: number;
    pending_total_cents: number;
  };
  recent_payments: Array<{
    id: string;
    type: "stripe";
    amount_cents: number;
    currency: string;
    customer_email: string;
    status: string;
    created_at: string;
    description: string;
  }>;
}

interface PixPayment {
  id: string;
  user_id: string;
  amount_cents: number;
  description: string | null;
  receipt_url: string | null;
  status: string;
  admin_notes: string | null;
  created_at: string;
  profiles?: { email: string } | null;
}

const formatCurrency = (cents: number) => {
  return `R$ ${(cents / 100).toFixed(2).replace(".", ",")}`;
};

export default function AdminFinancial() {
  const { loading: authLoading, isAuthenticated } = useAuth();
  const { role, isLoading: planLoading } = useUserPlan();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<FinancialData | null>(null);
  const [pixPayments, setPixPayments] = useState<PixPayment[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [activeTab, setActiveTab] = useState<"overview" | "payments" | "pix" | "subscriptions">("overview");

  // AI cost estimation params (loaded from localStorage or defaults)
  const [costPerAiResponse, setCostPerAiResponse] = useState(0);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate("/auth");
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!authLoading && !planLoading && isAuthenticated && role !== "admin") {
      toast({ title: "Acesso negado", variant: "destructive" });
      navigate("/dashboard");
    }
  }, [authLoading, planLoading, isAuthenticated, role, navigate, toast]);

  useEffect(() => {
    if (isAuthenticated && role === "admin") {
      fetchFinancialData();
      fetchPixPayments();
      calculateAiCostPerResponse();
    }
  }, [isAuthenticated, role, selectedMonth]);

  const calculateAiCostPerResponse = () => {
    const saved = localStorage.getItem("admin_cost_params");
    const params = saved ? JSON.parse(saved) : {
      avgQuestionSizeChars: 200,
      avgAnswerSizeChars: 150,
      avgContextSizeChars: 1500,
      avgQuestionsPerForm: 5,
      avgDocInputChars: 3000,
      avgDocOutputWords: 2000,
    };

    // Average cost per AI response (question generation + proportional doc cost)
    const questionsPerLead = params.avgQuestionsPerForm;
    let totalInputTokens = 0;
    let totalOutputTokens = 0;

    for (let q = 0; q < questionsPerLead; q++) {
      totalInputTokens += (params.avgContextSizeChars + q * (params.avgQuestionSizeChars + params.avgAnswerSizeChars) + params.avgAnswerSizeChars) * TOKENS_PER_CHAR;
      totalOutputTokens += params.avgQuestionSizeChars * TOKENS_PER_CHAR;
    }

    const questionsCost = (totalInputTokens / 1_000_000) * FLASH_INPUT_COST_PER_1M +
      (totalOutputTokens / 1_000_000) * FLASH_OUTPUT_COST_PER_1M;

    const docInputTokens = params.avgDocInputChars * TOKENS_PER_CHAR;
    const docOutputTokens = params.avgDocOutputWords * AVG_CHARS_PER_WORD * TOKENS_PER_CHAR;
    const docCost = (docInputTokens / 1_000_000) * PRO_INPUT_COST_PER_1M +
      (docOutputTokens / 1_000_000) * PRO_OUTPUT_COST_PER_1M;

    // Cost per "AI response" = (questions + doc) / questionsPerLead
    const costPerResponse = (questionsCost + docCost) / questionsPerLead;
    setCostPerAiResponse(costPerResponse * USD_TO_BRL); // in BRL
  };

  const fetchFinancialData = async () => {
    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) return;

      const { data: result, error } = await supabase.functions.invoke("admin-financial", {
        headers: { Authorization: `Bearer ${session.session.access_token}` },
        body: { action: "get_financial_summary", month: selectedMonth },
      });

      if (error) throw error;
      setData(result);
    } catch (error) {
      console.error("Error fetching financial data:", error);
      toast({ title: "Erro ao carregar dados financeiros", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchPixPayments = async () => {
    try {
      const startDate = new Date(`${selectedMonth}-01T00:00:00Z`);
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + 1);

      const { data: payments } = await supabase
        .from("pix_payments")
        .select("*")
        .gte("created_at", startDate.toISOString())
        .lt("created_at", endDate.toISOString())
        .order("created_at", { ascending: false });

      if (payments) {
        // Fetch emails for each user
        const userIds = [...new Set(payments.map(p => p.user_id))];
        const { data: profiles } = await supabase
          .from("profiles")
          .select("user_id, email")
          .in("user_id", userIds);

        const enriched = payments.map(p => ({
          ...p,
          profiles: profiles?.find(pr => pr.user_id === p.user_id) || null,
        }));
        setPixPayments(enriched);
      }
    } catch (error) {
      console.error("Error fetching PIX payments:", error);
    }
  };

  const handlePixAction = async (paymentId: string, action: "approved" | "rejected", notes?: string) => {
    try {
      const { error } = await supabase
        .from("pix_payments")
        .update({
          status: action,
          admin_notes: notes || null,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", paymentId);

      if (error) throw error;

      toast({ title: action === "approved" ? "Pagamento aprovado!" : "Pagamento rejeitado" });
      fetchPixPayments();
      fetchFinancialData();
    } catch (error) {
      console.error("Error updating PIX:", error);
      toast({ title: "Erro ao atualizar", variant: "destructive" });
    }
  };

  if (authLoading || planLoading || role !== "admin") {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const aiCostCents = data ? Math.round(data.costs.ai_responses * costPerAiResponse * 100) : 0;
  const totalCostsCents = (data?.costs.stripe_fees_cents || 0) + aiCostCents;
  const netRevenueCents = (data?.revenue.net_cents || 0);
  const taxesCents = Math.round(netRevenueCents * SIMPLES_TAX_RATE);
  const grossProfitCents = netRevenueCents - totalCostsCents - taxesCents;

  return (
    <div className="min-h-screen bg-gradient-hero">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/admin"><ArrowLeft className="h-4 w-4" /></Link>
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-primary rounded-xl flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="text-xl font-display font-bold">Painel Financeiro</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-40"
            />
            <Button variant="outline" size="icon" onClick={() => { fetchFinancialData(); fetchPixPayments(); }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Badge variant="secondary">
              <Shield className="mr-1 h-3 w-3" />Admin
            </Badge>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 pb-[100px]">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          {[
            { key: "overview", label: "Visão Geral", icon: TrendingUp },
            { key: "payments", label: "Pagamentos", icon: CreditCard },
            { key: "pix", label: "PIX", icon: Banknote },
            { key: "subscriptions", label: "Assinaturas", icon: Receipt },
          ].map(tab => (
            <Button
              key={tab.key}
              variant={activeTab === tab.key ? "default" : "outline"}
              onClick={() => setActiveTab(tab.key as any)}
            >
              <tab.icon className="mr-2 h-4 w-4" />
              {tab.label}
              {tab.key === "pix" && data && data.pix.pending_count > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 px-1.5 text-xs">
                  {data.pix.pending_count}
                </Badge>
              )}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map(i => (
              <Card key={i}><CardHeader><Skeleton className="h-4 w-1/2" /></CardHeader>
                <CardContent><Skeleton className="h-8 w-1/3" /></CardContent></Card>
            ))}
          </div>
        ) : data && (
          <>
            {/* Overview */}
            {activeTab === "overview" && (
              <div className="space-y-6">
                {/* Revenue Cards */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-emerald-500" />
                        Receita Bruta
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-emerald-600">{formatCurrency(data.revenue.total_cents)}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Stripe: {formatCurrency(data.revenue.stripe_cents)} | PIX: {formatCurrency(data.revenue.pix_cents)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <TrendingDown className="h-4 w-4 text-red-500" />
                        Custos Totais
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-red-500">{formatCurrency(totalCostsCents)}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Stripe: {formatCurrency(data.costs.stripe_fees_cents)} | IA: {formatCurrency(aiCostCents)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <Percent className="h-4 w-4 text-amber-500" />
                        Impostos (Simples)
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-amber-600">{formatCurrency(taxesCents)}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {(SIMPLES_TAX_RATE * 100).toFixed(0)}% sobre receita líquida
                      </div>
                    </CardContent>
                  </Card>

                  <Card className={grossProfitCents >= 0 ? "border-emerald-200" : "border-red-200"}>
                    <CardHeader className="pb-2">
                      <CardDescription className="flex items-center gap-2">
                        <PiggyBank className="h-4 w-4" />
                        Lucro Bruto
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className={`text-3xl font-bold ${grossProfitCents >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {formatCurrency(grossProfitCents)}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Margem: {netRevenueCents > 0 ? ((grossProfitCents / netRevenueCents) * 100).toFixed(1) : "0"}%
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Detailed Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>Breakdown Financeiro — {selectedMonth}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between py-2">
                        <span className="text-muted-foreground">Receita Stripe</span>
                        <span className="font-medium">{formatCurrency(data.revenue.stripe_cents)}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="text-muted-foreground">Receita PIX</span>
                        <span className="font-medium">{formatCurrency(data.revenue.pix_cents)}</span>
                      </div>
                      <div className="flex justify-between py-2 text-red-500">
                        <span>(-) Reembolsos</span>
                        <span>{formatCurrency(data.revenue.refunds_cents)}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between py-2 font-semibold">
                        <span>= Receita Líquida</span>
                        <span>{formatCurrency(netRevenueCents)}</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between py-2 text-red-500">
                        <span>(-) Taxas do Stripe (≈3.99% + R$0,39)</span>
                        <span>{formatCurrency(data.costs.stripe_fees_cents)}</span>
                      </div>
                      <div className="flex justify-between py-2 text-red-500">
                        <span>(-) Custo de IA ({data.costs.ai_responses} respostas)</span>
                        <span>{formatCurrency(aiCostCents)}</span>
                      </div>
                      <div className="flex justify-between py-2 text-amber-600">
                        <span>(-) Impostos Simples Nacional ({(SIMPLES_TAX_RATE * 100)}%)</span>
                        <span>{formatCurrency(taxesCents)}</span>
                      </div>
                      <Separator />
                      <div className={`flex justify-between py-2 font-bold text-lg ${grossProfitCents >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        <span>= Lucro Bruto</span>
                        <span>{formatCurrency(grossProfitCents)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Stats */}
                <div className="grid gap-4 md:grid-cols-3">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Assinaturas Ativas</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{data.subscriptions.active}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Cancelamentos no mês</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-red-500">{data.subscriptions.canceled_this_month}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>PIX Pendentes</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-amber-500">{data.pix.pending_count}</div>
                      {data.pix.pending_count > 0 && (
                        <div className="text-xs text-muted-foreground">
                          Total: {formatCurrency(data.pix.pending_total_cents)}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Payments Tab */}
            {activeTab === "payments" && (
              <Card>
                <CardHeader>
                  <CardTitle>Pagamentos Recentes (Stripe)</CardTitle>
                  <CardDescription>Últimos 20 pagamentos via Stripe</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.recent_payments.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                            Nenhum pagamento neste mês
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.recent_payments.map(payment => (
                          <TableRow key={payment.id}>
                            <TableCell className="text-sm">
                              {new Date(payment.created_at).toLocaleDateString("pt-BR")}
                            </TableCell>
                            <TableCell className="text-sm">{payment.customer_email}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{payment.description}</TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(payment.amount_cents)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                                <CheckCircle className="mr-1 h-3 w-3" />
                                Pago
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* PIX Tab */}
            {activeTab === "pix" && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Banknote className="h-5 w-5" />
                    Pagamentos PIX
                  </CardTitle>
                  <CardDescription>Gerencie comprovantes de pagamento PIX</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Usuário</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Comprovante</TableHead>
                        <TableHead>Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pixPayments.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                            Nenhum pagamento PIX neste mês
                          </TableCell>
                        </TableRow>
                      ) : (
                        pixPayments.map(pix => (
                          <TableRow key={pix.id}>
                            <TableCell className="text-sm">
                              {new Date(pix.created_at).toLocaleDateString("pt-BR")}
                            </TableCell>
                            <TableCell className="text-sm">{pix.profiles?.email || pix.user_id.slice(0, 8)}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{pix.description || "—"}</TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(pix.amount_cents)}
                            </TableCell>
                            <TableCell>
                              {pix.status === "pending" && (
                                <Badge variant="outline" className="text-amber-600 border-amber-300">
                                  <Clock className="mr-1 h-3 w-3" />Pendente
                                </Badge>
                              )}
                              {pix.status === "approved" && (
                                <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                                  <CheckCircle className="mr-1 h-3 w-3" />Aprovado
                                </Badge>
                              )}
                              {pix.status === "rejected" && (
                                <Badge variant="destructive">
                                  <XCircle className="mr-1 h-3 w-3" />Rejeitado
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {pix.receipt_url ? (
                                <Button variant="ghost" size="sm" asChild>
                                  <a href={pix.receipt_url} target="_blank" rel="noopener noreferrer">
                                    Ver
                                  </a>
                                </Button>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {pix.status === "pending" && (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                    onClick={() => handlePixAction(pix.id, "approved")}
                                  >
                                    <CheckCircle className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-red-600 border-red-300 hover:bg-red-50"
                                    onClick={() => handlePixAction(pix.id, "rejected")}
                                  >
                                    <XCircle className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Subscriptions Tab */}
            {activeTab === "subscriptions" && (
              <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Assinaturas Ativas</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-4xl font-bold text-emerald-600">{data.subscriptions.active}</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        MRR estimado: {formatCurrency(data.subscriptions.active * 2990)}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardDescription>Cancelamentos ({selectedMonth})</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-4xl font-bold text-red-500">{data.subscriptions.canceled_this_month}</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Churn: {data.subscriptions.active > 0
                          ? ((data.subscriptions.canceled_this_month / (data.subscriptions.active + data.subscriptions.canceled_this_month)) * 100).toFixed(1)
                          : "0"}%
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {data.subscriptions.canceled_details.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-red-500" />
                        Cancelamentos Recentes
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>ID da Assinatura</TableHead>
                            <TableHead>Data do Cancelamento</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.subscriptions.canceled_details.map(sub => (
                            <TableRow key={sub.id}>
                              <TableCell className="font-mono text-sm">{sub.id}</TableCell>
                              <TableCell>
                                {sub.canceled_at ? new Date(sub.canceled_at).toLocaleDateString("pt-BR") : "—"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
